import { contactsApi, dataVerifyApi, usersManagementApi } from 'app/services/api';
import _ from 'lodash';

import { SKIP_FORBIDDEN_RESPONSE_CONFIG } from 'constants/http';
import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { PromiseStatus } from 'constants/promise';
import { ACTIVITY_CONTACT_FIELDS } from 'constants/userSearch';
import { catchAsyncActionError } from 'utils/action';
import { getContactUserSearchFilters } from 'utils/activities';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectMasterCompanyId } from 'store/auth/selectors';

import { selectActivityContactLookup } from '../selectors';

export const CONTACTS_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/contacts`,
  {
    ADD_CONTACTS: 'ADD_CONTACTS',
    GET_APPLICATION_CONTACTS: 'GET_APPLICATION_CONTACTS',
  },
);

export const getApplicationContactsAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      const { data } = await contactsApi.getApplicationContacts({ applicationId });

      return dispatch({
        type: CONTACTS_ACTION_TYPE.GET_APPLICATION_CONTACTS,
        payload: data,
      });
    },
);

export const getLoanPartiesAction = catchAsyncActionError(({ applicationId }) => async () => {
  const { data } = await contactsApi.getLoanParties({ applicationId });

  return data ?? [];
});

export const getDriveReportPreviewAction = catchAsyncActionError(
  ({ companyId, loanId }) =>
    async () => {
      const { data } = await dataVerifyApi.getDriveReportPreview({ companyId, loanId });

      return data;
    },
);

const getFilterValues = ({ emailAddresses = [], phoneNumbers = [] }) =>
  emailAddresses.concat(phoneNumbers);

// Names are an enhancement: each search settles on its own, so a failed one only leaves its raw
// emails/phones on the activities and never toasts. Values a search answered, matched or not, are
// recorded so later refreshes don't search them again; failed ones are retried on the next refresh.
export const resolveActivityContactsAction = () => async (dispatch, getState) => {
  const state = getState();
  // Scoped like the GET /contacts it replaces: without a company, internal users would search
  // every tenant.
  const companyId = selectMasterCompanyId(state);
  const filters = getContactUserSearchFilters(selectActivityContactLookup(state));

  if (_.isNull(companyId) || !filters.length) {
    return null;
  }

  const responses = await Promise.allSettled(
    filters.map(filter =>
      usersManagementApi.searchUsers(
        { fields: ACTIVITY_CONTACT_FIELDS, filter: { ...filter, companyId } },
        SKIP_FORBIDDEN_RESPONSE_CONFIG,
      ),
    ),
  );

  const answered = responses
    .map((response, index) => ({ response, filter: filters[index] }))
    .filter(({ response }) => response.status === PromiseStatus.FULFILLED);

  if (!answered.length) {
    return null;
  }

  return dispatch({
    type: CONTACTS_ACTION_TYPE.ADD_CONTACTS,
    payload: {
      contacts: answered.flatMap(({ response }) => response.value?.data?.items ?? []),
      lookedUpValues: answered.flatMap(({ filter }) => getFilterValues(filter)),
    },
  });
};
